import React, { useCallback, useMemo, useState } from 'react';
import { isNull, isUndefined, noop } from 'lodash/fp';

import { PlusButton } from '../button/PlusButton';
import { Icon } from '../icon/Icon';
import { Delete, Tick } from '../icons';
import styles from './Table.module.scss';
import { Spinner } from '../spinner/Spinner';

const { green, red } = styles;

interface SelectCellProps {
    select: (selectedId: number | string) => void;
    selectDisabled?: (selectedId: number) => boolean;
    deselect?: (selectedId: number | string) => void;
    selectedIds: number[] | string[] | null;
    value: number | string;
    isLoading?: boolean;
}

export const SelectCell: React.FC<SelectCellProps> = ({ select, selectedIds, value, selectDisabled = () => false, isLoading = false, deselect }) => {
    const [showRemoveIcon, setShowRemoveIcon] = useState(false);

    const selectCell = () => select(value);
    const deselectCell = useCallback(() => !isUndefined(deselect) ? deselect(value) : noop, [deselect, value]);
    const selected = useMemo(() => {
        if (isNull(selectedIds)) {
            return false;
        }
        if (typeof value === 'string') {
            return (selectedIds as string[]).includes(value);
        }
        return (selectedIds as number[]).includes(value);
    }, [selectedIds, value]);

    const isDisabled = typeof value === 'number' && selectDisabled(value);

    const selectedIcon = useMemo(() => showRemoveIcon ? Delete : Tick, [showRemoveIcon]);
    const selectedIconFill = useMemo(() => showRemoveIcon ? red : green, [showRemoveIcon]);

    if (isLoading) {
        return (
            <div className={styles.selectCell}>
                <Spinner size={18} />
            </div>
        );
    }

    if (selected) {
        return (
            <div className={styles.selectCell} style={{ cursor: !isUndefined(deselect) ? 'pointer' : 'auto' }} onClick={deselectCell} onMouseEnter={() => !isUndefined(deselect) && setShowRemoveIcon(true)} onMouseLeave={() => setShowRemoveIcon(false)}>
                <Icon icon={selectedIcon} fontSize={24} color={selectedIconFill} />
            </div>
        );
    }

    return (
        <div className={styles.selectCell}>
            <PlusButton onClick={selectCell} fontSize={18} disabled={isDisabled} />
        </div>
    );
};
